import { useCallback, useEffect, useState } from 'react';
import { ListParams } from '../services/crudFactory';
import { PaginatedResponse } from '../types/common';

export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface UsePaginatedResourceOptions {
  initialParams?: ListParams;
  initialPageSize?: number;
  immediate?: boolean;
}

export interface UsePaginatedResourceResult<T> {
  data: T[];
  loading: boolean;
  error: unknown;
  pagination: PaginationState;
  params: ListParams;
  setParams: (next: ListParams | ((prev: ListParams) => ListParams)) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refresh: () => Promise<void>;
}

export const usePaginatedResource = <T>(
  listFn: (params?: ListParams) => Promise<PaginatedResponse<T>>,
  options?: UsePaginatedResourceOptions,
): UsePaginatedResourceResult<T> => {
  const {
    initialParams,
    initialPageSize = 25,
    immediate = true,
  } = options ?? {};

  const [params, setParams] = useState<ListParams>({
    page: 1,
    page_size: initialPageSize,
    ...initialParams,
  });
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: Number(params.page) || 1,
    pageSize: Number(params.page_size) || initialPageSize,
    total: 0,
    hasNext: false,
    hasPrevious: false,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listFn(params);
      setData(response.results);
      setPagination({
        page: Number(params.page) || 1,
        pageSize: Number(params.page_size) || initialPageSize,
        total: response.count,
        hasNext: response.next !== null,
        hasPrevious: response.previous !== null,
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [listFn, params, initialPageSize]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate]);

  const setPage = (page: number) =>
    setParams((prev) => ({
      ...prev,
      page,
    }));

  const setPageSize = (pageSize: number) =>
    setParams((prev) => ({
      ...prev,
      page: 1,
      page_size: pageSize,
    }));

  return {
    data,
    loading,
    error,
    pagination,
    params,
    setParams,
    setPage,
    setPageSize,
    refresh: fetchData,
  };
};
